import cloneDeep from "lodash/cloneDeep";
import isPlainObject from "lodash/isPlainObject";
import transform from "lodash/transform";
import type { BaseBlock, BaseProp } from "../DataModel.types";
import type { TreeItem } from "./LayersTreeItem";
import {
  type ChildrenProp,
  extractChildBlocks,
  isChildrenProp,
} from "./tree-utils";

export interface ExtendedTreeItem extends TreeItem {
  children?: ExtendedTreeItem[];
  isPropertyNode?: boolean;
}

export const buildBlockMap = (
  originalBlocks: BaseBlock[],
): Map<string, BaseBlock> =>
  transform(
    originalBlocks,
    (acc: Map<string, BaseBlock>, block: BaseBlock) => {
      const stack = [block];
      while (stack.length) {
        const b = stack.pop();
        if (!b) continue;
        if (acc.has(b.id)) continue;
        acc.set(b.id, b);
        if (!b.props) continue;

        const childBlocks = extractChildBlocks(b.props);
        stack.push(...childBlocks);
      }
    },
    new Map<string, BaseBlock>(),
  );

export const isPropertyNode = (item: TreeItem): boolean =>
  (item as ExtendedTreeItem).isPropertyNode === true;

export const getPropertyNodes = (children: TreeItem[]): ExtendedTreeItem[] =>
  children.filter((child): child is ExtendedTreeItem => isPropertyNode(child));

export const getRegularChildren = (children: TreeItem[]): TreeItem[] =>
  children.filter(child => !isPropertyNode(child));

export const createUpdatedChildrenProp = (
  originalProp: ChildrenProp,
  children: TreeItem[],
  rebuildFromTree: (items: TreeItem[]) => BaseBlock[],
): ChildrenProp => ({
  ...originalProp,
  value: rebuildFromTree(children),
});

export const rebuildPropsWithPropertyNodes = (
  props: Record<string, unknown>,
  propertyNodes: ExtendedTreeItem[],
  rebuildFromTree: (items: TreeItem[]) => BaseBlock[],
): Record<string, BaseProp> => {
  const sortedProps: Record<string, BaseProp> = {};

  for (const [key, value] of Object.entries(props)) {
    if (!isChildrenProp(value)) {
      sortedProps[key] = value as BaseProp;
    }
  }

  for (const propNode of propertyNodes) {
    const propName = propNode.type;
    const originalProp = props[propName];

    if (originalProp && isChildrenProp(originalProp)) {
      sortedProps[propName] = createUpdatedChildrenProp(
        originalProp,
        propNode.children || [],
        rebuildFromTree,
      );
    }
  }

  for (const [key, value] of Object.entries(props)) {
    if (isChildrenProp(value) && !(key in sortedProps)) {
      sortedProps[key] = value;
    }
  }

  return sortedProps;
};

export const rebuildPropsWithRegularChildren = (
  props: Record<string, unknown>,
  regularChildren: TreeItem[],
  rebuildFromTree: (items: TreeItem[]) => BaseBlock[],
): Record<string, BaseProp> => {
  const newProps = { ...props } as Record<string, BaseProp>;

  for (const [key, prop] of Object.entries(newProps)) {
    if (isChildrenProp(prop)) {
      newProps[key] = createUpdatedChildrenProp(
        prop,
        regularChildren,
        rebuildFromTree,
      );
      break;
    }
  }

  return newProps;
};

export const updateBlockProps = (
  props: Record<string, unknown>,
  item: TreeItem,
  rebuildFromTree: (items: TreeItem[]) => BaseBlock[],
): Record<string, BaseProp> => {
  if (!item.children || item.children.length === 0) {
    return props as Record<string, BaseProp>;
  }

  const propertyNodes = getPropertyNodes(item.children);
  const regularChildren = getRegularChildren(item.children);

  if (propertyNodes.length > 0) {
    return rebuildPropsWithPropertyNodes(props, propertyNodes, rebuildFromTree);
  }

  if (regularChildren.length > 0) {
    return rebuildPropsWithRegularChildren(
      props,
      regularChildren,
      rebuildFromTree,
    );
  }

  return props as Record<string, BaseProp>;
};

export const processBlock = (
  item: TreeItem,
  blockMap: Map<string, BaseBlock>,
  usedBlockIds: Set<string>,
  rebuildFromTree: (items: TreeItem[]) => BaseBlock[],
): BaseBlock | null => {
  if (isPropertyNode(item)) return null;
  if (usedBlockIds.has(item.id)) return null;

  const originalBlock = blockMap.get(item.id);
  if (!originalBlock) return null;

  usedBlockIds.add(item.id);
  const newBlock = cloneDeep(originalBlock);

  if (newBlock.props && isPlainObject(newBlock.props)) {
    newBlock.props = updateBlockProps(newBlock.props, item, rebuildFromTree);
  }

  return newBlock;
};
